import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete"
import type { FocusEventHandler, Ref } from "react"

import { gql } from "__generated__"
import { useQuery } from "@apollo/client"
import { Autocomplete, Checkbox, FormControl, TextField } from "@mui/material"

const FETCH_ATTENDEE_AUTOCOMPLETE_OPTIONS = gql(`
  query FetchAttendeeAutocompleteOptions {
    attendees {
      id
      name
      discordId
    }
  }
`)

type CommonProps = {
  className?: string
  disabled?: boolean
  error?: boolean
  excludeAttendeeIds?: number[]
  helperText?: string
  inputRef?: Ref<HTMLInputElement>
  label: string
  loadErrorText?: string
  noOptionsText?: string
  onBlur?: FocusEventHandler<HTMLDivElement>
  placeholder?: string
  required?: boolean
  size?: "medium" | "small"
}

type MultipleProps = {
  multiple: true
  onChange: (attendees: Attendee[]) => void
  value: Attendee[]
} & CommonProps

type SingleProps = {
  multiple?: false
  onChange: (attendee: Attendee | null) => void
  value: Attendee | null
} & CommonProps

type Props = MultipleProps | SingleProps

const AttendeeAutocomplete = ({
  className,
  disabled = false,
  error = false,
  excludeAttendeeIds = [],
  helperText,
  inputRef,
  label,
  loadErrorText = "Không thể tải danh sách nhân viên",
  noOptionsText = "Không có nhân viên phù hợp",
  onBlur,
  placeholder = "Tìm kiếm",
  required = false,
  size = "medium",
  ...selectionProps
}: Props) => {
  const { data, error: queryError, loading } = useQuery(FETCH_ATTENDEE_AUTOCOMPLETE_OPTIONS)
  const excludedIds = new Set(excludeAttendeeIds)
  const options: Attendee[] = (data?.attendees ?? [])
    .filter((attendee) => !excludedIds.has(attendee.id))
    .map((attendee) => ({
      discordId: attendee.discordId,
      id: attendee.id,
      name: attendee.name,
    }))

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      error={error || Boolean(queryError)}
      helperText={helperText || (queryError ? loadErrorText : undefined)}
      InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
      inputRef={inputRef}
      label={label}
      placeholder={placeholder}
      required={required}
    />
  )

  const commonProps = {
    disabled: disabled || Boolean(queryError),
    getOptionLabel: (option: Attendee) => option.name || option.discordId,
    isOptionEqualToValue: (option: Attendee, value: Attendee) => option.id === value.id,
    loading,
    loadingText: "Đang tải danh sách...",
    noOptionsText,
    onBlur,
    options,
    renderInput,
    size,
  }

  return (
    <FormControl className={className} fullWidth>
      {selectionProps.multiple ? (
        <Autocomplete
          {...commonProps}
          disableCloseOnSelect
          limitTags={2}
          multiple
          onChange={(_, value) => selectionProps.onChange(value)}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Checkbox checked={selected} />
              {option.name || option.discordId}
            </li>
          )}
          value={selectionProps.value}
        />
      ) : (
        <Autocomplete
          {...commonProps}
          autoHighlight
          onChange={(_, value) => selectionProps.onChange(value)}
          value={selectionProps.value}
        />
      )}
    </FormControl>
  )
}

export default AttendeeAutocomplete
